/**
 * How a finished 3DS challenge tells the checkout modal it is done.
 *
 * On web the bank page lives in an iframe. After OTP, Wompi redirects that
 * iframe to `urlRedirect`. If that URL is production (`app.tago.rent`) while
 * the guest is on localhost:8080, two things break at once:
 *
 *   1. The iframe becomes cross-origin, so ThreeDsModal cannot see the
 *      navigation and never calls confirm-payment.
 *   2. The production payment-complete page talks to the production project,
 *      not fixtures, and paints "no se pudo" over a charge that fixtures
 *      still has as pending.
 *
 * The fixtures `3ds-return` function (HTTPS, so Wompi accepts it) and the
 * local payment-complete page both post this message to the parent. The modal
 * listens and confirms against the project the guest is actually using.
 */
export const THREE_DS_COMPLETE_TYPE = 'tago.3ds.complete'

export const THREE_DS_RETURN_PATH = '/booking/payment-complete'
export const THREE_DS_TOURS_RETURN_PATH = '/tours/payment-complete'
export const THREE_DS_SPLIT_RETURN_PREFIX = '/split/'
export const THREE_DS_HOP_PATH = '/functions/v1/threeds-return'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1'])
const PRODUCT_HOSTS = new Set([
  'tago.app',
  'app.tago.rent',
  'api.tago.rent',
  'tago.rent',
  'www.tago.rent',
])

/**
 * Named ReturnUrl policy. Local checkouts must stay on the origin they
 * started from. `app.tago.rent` is ONLY the fallback when the hop has no
 * allowed returnTo — never a silent bounce for localhost.
 */
export const PAYMENT_RETURN_URL_POLICY = {
  productFallback: `https://app.tago.rent${THREE_DS_RETURN_PATH}`,
} as const

export const THREE_DS_PARENT_ORIGINS = [
  'https://tago.app',
  'https://app.tago.rent',
  'https://tago.rent',
  'https://www.tago.rent',
  'http://localhost:8080',
  'http://localhost:8081',
  'http://localhost:8083',
  'http://localhost:19006',
  'http://localhost:3000',
] as const

/**
 * Deno's edge type library intentionally exposes only a small `Window`
 * surface, while the browser page uses the standard iframe/opener channel.
 * Keep that boundary explicit instead of weakening the message payload type.
 */
type ThreeDsMessageTarget = {
  postMessage(message: unknown, targetOrigin: string): void
}

type ThreeDsWindow = ThreeDsMessageTarget & {
  parent?: ThreeDsMessageTarget | null
  opener?: ThreeDsMessageTarget | null
}

export function postThreeDsCompleteToOpener(params: {
  transactionId?: string
  rentalId?: string
  provider?: string
}): void {
  if (typeof window === 'undefined') return
  const payload = buildThreeDsCompleteMessage(params)
  const currentWindow = window as unknown as ThreeDsWindow
  const targets = [currentWindow.parent, currentWindow.opener].filter(
    (target): target is ThreeDsMessageTarget => Boolean(target) && target !== currentWindow,
  )
  if (targets.length === 0) return
  for (const target of targets) {
    for (const origin of THREE_DS_PARENT_ORIGINS) {
      try {
        target.postMessage(payload, origin)
      } catch {
        // Wrong targetOrigin is dropped; never fail the page over it.
      }
    }
  }
}

export type ThreeDsCompleteMessage = {
  type: typeof THREE_DS_COMPLETE_TYPE
  transactionId?: string
  rentalId?: string
  provider?: string
}

export function isLocalCheckoutHost(hostname: string): boolean {
  return LOCAL_HOSTS.has(hostname.toLowerCase())
}

export function isThreeDsReturnHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  if (PRODUCT_HOSTS.has(host) || LOCAL_HOSTS.has(host)) return true
  return host.endsWith('.supabase.co') || host.endsWith('.supabase.net')
}

export function isAllowedThreeDsMessageOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    if (isLocalCheckoutHost(url.hostname) && url.protocol !== 'http:') return false
    if (!isLocalCheckoutHost(url.hostname) && url.protocol !== 'https:') return false
    return isThreeDsReturnHost(url.hostname)
  } catch {
    return false
  }
}

export function isThreeDsCheckoutReturnUrl(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl)
    if (!isThreeDsReturnHost(url.hostname)) return false
    if (url.pathname === THREE_DS_RETURN_PATH || url.pathname === THREE_DS_TOURS_RETURN_PATH) {
      return true
    }
    if (
      url.pathname.startsWith(THREE_DS_SPLIT_RETURN_PREFIX) &&
      url.pathname.length > THREE_DS_SPLIT_RETURN_PREFIX.length
    ) {
      return true
    }
    return url.pathname.includes(THREE_DS_HOP_PATH)
  } catch {
    return false
  }
}

export function parseThreeDsCompleteMessage(data: unknown): ThreeDsCompleteMessage | null {
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  if (record.type !== THREE_DS_COMPLETE_TYPE) return null
  return {
    type: THREE_DS_COMPLETE_TYPE,
    transactionId: typeof record.transactionId === 'string' ? record.transactionId : undefined,
    rentalId: typeof record.rentalId === 'string' ? record.rentalId : undefined,
    provider: typeof record.provider === 'string' ? record.provider : undefined,
  }
}

export function buildThreeDsCompleteMessage(params: {
  transactionId?: string
  rentalId?: string
  provider?: string
}): ThreeDsCompleteMessage {
  return {
    type: THREE_DS_COMPLETE_TYPE,
    transactionId: params.transactionId,
    rentalId: params.rentalId,
    provider: params.provider,
  }
}

export function checkoutReturnPathFor(
  pathname?: string | null,
): typeof THREE_DS_RETURN_PATH | typeof THREE_DS_TOURS_RETURN_PATH {
  return pathname && pathname.includes('/tours/')
    ? THREE_DS_TOURS_RETURN_PATH
    : THREE_DS_RETURN_PATH
}

export function safeThreeDsAppReturnUrl(
  returnTo: string,
  allowedOrigins: readonly string[],
  fallback = PAYMENT_RETURN_URL_POLICY.productFallback,
): string {
  if (!returnTo) return fallback
  try {
    const url = new URL(returnTo)
    if (!allowedOrigins.includes(url.origin)) return fallback
    const path = url.pathname.startsWith(THREE_DS_SPLIT_RETURN_PREFIX)
      ? url.pathname
      : url.pathname === THREE_DS_TOURS_RETURN_PATH
        ? THREE_DS_TOURS_RETURN_PATH
        : THREE_DS_RETURN_PATH
    return `${url.origin}${path}`
  } catch {
    return fallback
  }
}

/** HTTPS hop Wompi can return to, carrying the rental/booking and the real app origin. */
export function buildThreeDsHopUrl(params: {
  supabaseUrl: string
  rentalId?: string
  bookingId?: string
  returnTo: string
}): string {
  const hop = new URL(`${params.supabaseUrl.replace(/\/$/, '')}${THREE_DS_HOP_PATH}`)
  if (params.rentalId) hop.searchParams.set('rentalId', params.rentalId)
  if (params.bookingId) hop.searchParams.set('bookingId', params.bookingId)
  hop.searchParams.set('returnTo', params.returnTo)
  return hop.toString()
}
